import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sparkles, X, Send, Mic, Square, Volume2, VolumeX, Loader2 } from "lucide-react";
import { API } from "../lib/api";
import { getVoice, setVoice, createSpeaker } from "../lib/voice";

// Owner-bound assistant session: the server issues an id + secret key; both are required to read or extend the conversation.
const getSession = async () => {
  const cached = localStorage.getItem("dk_ai_session_v2");
  if (cached) return JSON.parse(cached);
  const r = await fetch(`${API}/assistant/session`, { method: "POST" });
  const s = await r.json();
  localStorage.setItem("dk_ai_session_v2", JSON.stringify(s));
  localStorage.removeItem("dk_ai_session");
  return s;
};

// Render plain text with /relative-paths turned into links.
const Rich = ({ text }) => {
  const parts = text.split(/(\/[a-z0-9\-/#?=&]+)/gi);
  return <>{parts.map((p, i) => (/^\/[a-z0-9\-/#?=&]+$/i.test(p) && p.length > 1 ? <Link key={`${p}-${i}`} to={p} className="underline font-semibold text-[#7a5a0f]">{p}</Link> : <span key={`${p.slice(0, 24)}-${i}`}>{p}</span>))}</>;
};

export const VoiceSetting = ({ className = "" }) => {
  const [v, setV] = useState(getVoice());
  useEffect(() => { const h = (e) => setV(e.detail); window.addEventListener("dk-voice", h); return () => window.removeEventListener("dk-voice", h); }, []);
  return (
    <fieldset className={`inline-flex items-center gap-1 text-[11px] ${className}`} data-testid="voice-setting">
      <legend className="sr-only">Voice for spoken content</legend>
      {[["female", "Female voice"], ["male", "Male voice"]].map(([k, l]) => (
        <button key={k} type="button" onClick={() => { setVoice(k); setV(k); }} aria-pressed={v === k} className={`px-2 py-1 rounded-full border ${v === k ? "bg-[#155e3f] text-white border-[#0d1724]" : "border-black/15 text-[#0a0d12]/70"}`} data-testid={`voice-${k}`}>{l}</button>
      ))}
    </fieldset>
  );
};

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [speakReplies, setSpeakReplies] = useState(false);
  const [rec, setRec] = useState(null);
  const [speaking, setSpeaking] = useState(false);
  const speaker = useRef(createSpeaker());
  const listRef = useRef(null);
  const loc = useLocation();

  useEffect(() => {
    if (!open || msgs.length) return;
    getSession().then((s) => fetch(`${API}/assistant/history/${s.session_id}`, { headers: { "X-Session-Key": s.session_key } })).then((r) => r.json()).then((d) => setMsgs(d.messages?.length ? d.messages : [{ role: "assistant", content: "Hello! I'm the Dutsen Kauna AI Assistant. I can explain our programmes, help you apply for support, volunteer, donate or find learning resources. Applying is always free. What can I help you with?" }])).catch(() => {});
  }, [open, msgs.length]);
  useEffect(() => { listRef.current?.scrollTo({ top: 1e9 }); }, [msgs]);
  useEffect(() => () => speaker.current.stop(), []);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    try {
      const s = await getSession(); const r = await fetch(`${API}/assistant/chat`, { method: "POST", headers: { "Content-Type": "application/json", "X-Session-Key": s.session_key }, body: JSON.stringify({ session_id: s.session_id, message: q, page: loc.pathname }) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "The assistant is unavailable right now.");
      const reader = r.body.getReader(); const dec = new TextDecoder(); let buf = "", full = "";
      for (;;) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n"); buf = lines.pop();
        for (const l of lines) {
          if (!l.startsWith("data: ")) continue;
          const ev = JSON.parse(l.slice(6));
          if (ev.delta) { full += ev.delta; setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: full }; return c; }); }
        }
      }
      if (speakReplies && full) { setSpeaking(true); speaker.current.speak(full, getVoice(), (s) => setSpeaking(!!(s.playing || s.loading))).catch(() => setSpeaking(false)); }
    } catch (e) {
      setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: e.message }; return c; });
    } finally { setBusy(false); }
  };

  const toggleMic = async () => {
    if (rec) { rec.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream); const chunks = [];
      mr.ondataavailable = (e) => chunks.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop()); setRec(null);
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        const fd = new FormData(); fd.append("audio", blob, "speech.webm");
        setBusy(true);
        try { const r = await fetch(`${API}/stt`, { method: "POST", body: fd }); const d = await r.json(); if (!r.ok) throw new Error(d.detail); setBusy(false); if (d.text) send(d.text); else setInput(""); }
        catch (e) { setBusy(false); setMsgs((m) => [...m, { role: "assistant", content: e.message || "I could not hear that clearly — please try again or type your question." }]); }
      };
      mr.start(); setRec(mr);
      setTimeout(() => { if (mr.state === "recording") mr.stop(); }, 30000);
    } catch { setMsgs((m) => [...m, { role: "assistant", content: "Microphone access was blocked. You can type your question instead." }]); }
  };

  return (
    <div className="fixed left-3 md:left-5 top-[calc(50%-92px)] z-50 flex flex-col md:flex-row-reverse items-start md:items-end gap-3" data-testid="ai-assistant">
      {open && (
        <div className="w-[min(92vw,380px)] max-md:fixed max-md:inset-x-3 max-md:bottom-4 max-md:w-auto bg-white rounded-2xl shadow-2xl border border-black/5 overflow-hidden flex flex-col max-h-[70vh]" role="dialog" aria-label="Dutsen Kauna AI Assistant" data-testid="ai-panel">
          <div className="bg-[#155e3f] text-white p-4 flex items-start justify-between gap-2">
            <div><div className="text-[10px] uppercase tracking-[0.2em] text-[#d6aa43] font-semibold">Dutsen Kauna AI Assistant</div><p className="text-xs text-white mt-1">Information and guidance only — staff make all decisions. Don't share ID or bank details here.</p></div>
            <button onClick={() => { setOpen(false); speaker.current.stop(); }} aria-label="Close assistant" data-testid="ai-close"><X size={18} /></button>
          </div>
          <div className="px-3 py-2 border-b border-black/5 flex items-center justify-between gap-2 flex-wrap bg-[#f8f4e8]">
            <VoiceSetting />
            <button type="button" onClick={() => { setSpeakReplies((s) => !s); if (speakReplies) speaker.current.stop(); }} aria-pressed={speakReplies} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0d1724]" data-testid="ai-speak-toggle">{speakReplies ? <Volume2 size={14} /> : <VolumeX size={14} />} {speakReplies ? "Spoken replies on" : "Read replies aloud"}</button>
          </div>
          <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-3 text-sm" aria-live="polite" data-testid="ai-messages">
            {msgs.map((m, i) => (
              <div key={m.id || `${m.role}-${m.at || i}`} className={`max-w-[90%] rounded-2xl px-3 py-2 leading-relaxed ${m.role === "user" ? "ml-auto bg-[#155e3f] text-white" : "bg-[#f8f4e8] text-[#0a0d12]"}`} data-testid={`ai-msg-${m.role}`}>
                {m.content ? <Rich text={m.content} /> : <Loader2 size={14} className="animate-spin" />}
              </div>
            ))}
          </div>
          {speaking && <button onClick={() => { speaker.current.stop(); setSpeaking(false); }} className="mx-3 mb-1 text-[11px] underline text-[#7a5a0f] self-start" data-testid="ai-stop-speaking">Stop reading</button>}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-black/5 flex items-center gap-2">
            <button type="button" onClick={toggleMic} aria-label={rec ? "Stop recording" : "Speak your question"} className={`p-2.5 rounded-full border ${rec ? "bg-red-600 text-white border-red-600 animate-pulse" : "border-black/15 text-[#0d1724]"}`} data-testid="ai-mic">{rec ? <Square size={16} /> : <Mic size={16} />}</button>
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={rec ? "Listening… tap the square when done" : "Ask about programmes, applying, volunteering…"} aria-label="Your question" className="flex-1 px-3 py-2.5 border border-black/15 rounded-full text-sm min-w-0" data-testid="ai-input" />
            <button type="submit" disabled={busy || !input.trim()} aria-label="Send" className="p-2.5 rounded-full bg-[#d6aa43] text-[#0a0d12] disabled:opacity-40" data-testid="ai-send">{busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}</button>
          </form>
        </div>
      )}
      <button onClick={() => setOpen((o) => !o)} className="h-14 w-14 rounded-full bg-[#155e3f] text-[#d6aa43] shadow-xl flex items-center justify-center hover:scale-105 transition-transform border-2 border-[#d6aa43]/60" aria-label={open ? "Close AI assistant" : "Open Dutsen Kauna AI Assistant"} aria-expanded={open} data-testid="ai-toggle">
        {open ? <X size={24} /> : <Sparkles size={24} />}
      </button>
    </div>
  );
}
